import React, { useState, useEffect } from 'react';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { playSound } from '../Board/BoardConstants';
import confetti from 'canvas-confetti';
import toast from 'react-hot-toast';

// 🔥 IMPORTING GLOBAL CONSTANTS
import { PIECE_IMAGES } from '../../utils/AppConstants';

const STAR_ICON = 'https://cdn.jsdelivr.net/gh/lichess-org/lila@master/public/piece/cburnett/star.svg'; 
const FUN_POPUPS = ["YUMMY! 🍎", "CRUNCH! 🍏", "AWESOME! 🚀", "DELICIOUS! 🤤", "CHOMP! 🦖", "PERFECT! ⭐"];

const fireConfetti = () => { confetti({ particleCount: 150, spread: 80, origin: { y: 0.5 }, zIndex: 9999 }); };

// ==========================================
// 🧠 MINI GAME 1: MULTIPLE CHOICE QUIZ (MCQ)
// ==========================================
const MCQGame = ({ data, safeEmail, activeClass }) => {
    const [selected, setSelected] = useState(null);
    const [mistakes, setMistakes] = useState(0);

    useEffect(() => {
        setSelected(null);
        setMistakes(0);
    }, [data.startTime]);

    const handleSelect = (option) => {
        if (selected === data.answer) return; 
        setSelected(option);
        
        if (option === data.answer) {
            playSound('notify'); fireConfetti();
            updateDoc(doc(db, "classes", activeClass.id), { 
                [`studentArcadeStats.${safeEmail}`]: { completed: true, stars: 3, selected: option, mistakes: mistakes } 
            });
        } else {
            playSound('error');
            const newMistakes = mistakes + 1;
            setMistakes(newMistakes);
            updateDoc(doc(db, "classes", activeClass.id), { 
                [`studentArcadeStats.${safeEmail}`]: { mistakes: newMistakes, completed: false, selected: option } 
            });
        }
    };

    return (
        <div style={{width: '100%', maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
            <h1 style={{color: 'var(--text-primary)', textAlign: 'center', fontSize: '28px', lineHeight: '1.4' }}>{data.question}</h1>
            {data.image && <img src={PIECE_IMAGES[data.image]} alt="piece" style={{width: '140px', filter: 'drop-shadow(0 10px 15px rgba(0,0,0,0.5))' }} />}
            
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', width: '100%', marginTop: '20px' }}>
                {data.options.map(opt => {
                    let bg = 'var(--bg-primary)'; let border = '2px solid rgba(163, 177, 198, 0.4)';
                    if (selected === opt) {
                        if (opt === data.answer) { bg = '#10b981'; border = '2px solid #047857'; } 
                        else { bg = '#ef4444'; border = '2px solid #b91c1c'; } 
                    } else if (selected === data.answer && opt === data.answer) {
                        bg = '#10b981'; border = '2px solid #047857';
                    }
                    return (
                        <button key={opt} onClick={() => handleSelect(opt)} style={{padding: '20px', fontSize: '20px', fontWeight: 'bold', color: 'white', background: bg, border: border, borderRadius: '12px', cursor: 'pointer', transition: '0.2s', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
                            {opt}
                        </button>
                    )
                })}
            </div>

            {mistakes >= 1 && selected !== data.answer && (
                <div style={{background: 'var(--bg-primary)', color: '#fde047', padding: '15px', borderRadius: '8px', border: '1px solid #fde047', width: '100%', textAlign: 'center', fontWeight: 'bold', marginTop: '10px', fontSize: '18px', animation: 'pulse 1s infinite' }}>
                    💡 Hint: {data.hint}
                </div>
            )}
        </div>
    );
};

// ==========================================
// 📍 MINI GAME 2: BOARD AWARENESS
// ==========================================
const AwarenessGame = ({ data, safeEmail, activeClass, boardWidth }) => {
    const [foundTargets, setFoundTargets] = useState([]);
    const [mistakes, setMistakes] = useState(0);

    useEffect(() => {
        setFoundTargets([]);
        setMistakes(0);
    }, [data.startTime]);

    const handleClick = (id) => {
        if (foundTargets.length === data.targets.length) return;

        if (data.targets.includes(id)) {
            playSound('notify');
            if (!foundTargets.includes(id)) {
                const newFound = [...foundTargets, id];
                setFoundTargets(newFound);
                const isDone = newFound.length === data.targets.length;
                if (isDone) fireConfetti();
                updateDoc(doc(db, "classes", activeClass.id), { 
                    [`studentArcadeStats.${safeEmail}`]: { 
                        foundTargets: newFound, 
                        mistakes: mistakes, 
                        completed: isDone, 
                        stars: isDone ? 3 : newFound.length 
                    } 
                });
            }
        } else {
            playSound('error');
            const newMistakes = mistakes + 1;
            setMistakes(newMistakes);
            updateDoc(doc(db, "classes", activeClass.id), { 
                [`studentArcadeStats.${safeEmail}`]: { 
                    foundTargets: foundTargets, 
                    mistakes: newMistakes, 
                    completed: false, 
                    stars: foundTargets.length 
                } 
            });
        }
    };

    const rows = [8,7,6,5,4,3,2,1]; const cols = ['a','b','c','d','e','f','g','h'];
    return (
        <div style={{ width: `${boardWidth}px`, maxWidth: 'min(65vh, 95vw)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{background: '#8b5cf6', padding: '15px', borderRadius: '12px', color: 'var(--text-primary)', textAlign: 'center', fontWeight: 'bold', fontSize: '20px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)' }}>
                {data.question} ({foundTargets.length}/{data.targets.length} found)
            </div>
            
            <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)`, border: '4px solid var(--bg-primary)', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 20px rgba(0,0,0,0.5)' }}>
                {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                    const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                    const isFound = foundTargets.includes(id);
                    const showHint = mistakes >= 3 && data.targets.includes(id) && !isFound;

                    return (
                        <div key={id} onClick={() => handleClick(id)} style={{background: isFound ? '#10b981' : (showHint ? '#fde047' : (isDark ? '#769656' : '#eeeed2')), display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: '0.2s', border: isFound ? '2px solid white' : 'none' }}>
                            {isFound && <span style={{fontSize:'35px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'}}>✅</span>}
                            {showHint && <span style={{fontSize:'25px', animation: 'pulse 1s infinite'}}>📍</span>}
                            <span style={{position: 'absolute', bottom: 2, right: 4, fontSize: '10px', color: isDark ? '#eeeed2' : '#769656', opacity: 0.5 }}>{id}</span>
                        </div>
                    );
                }))}
            </div>

            {mistakes >= 2 && foundTargets.length !== data.targets.length && (
                <div style={{background: 'var(--bg-primary)', color: '#fde047', padding: '12px', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>💡 Hint: {data.hint}</div>
            )}
        </div>
    );
};

// ==========================================
// 🧮 MATH GAME
// ==========================================
const MathGame = ({ data, safeEmail, activeClass }) => {
    const [selected, setSelected] = useState(null);
    const [mistakes, setMistakes] = useState(0);

    useEffect(() => { setSelected(null); setMistakes(0); }, [data.startTime]);

    const handleSelect = (option) => {
        if (selected === data.answer) return; 
        setSelected(option);
        if (option === data.answer) {
            playSound('notify'); fireConfetti();
            updateDoc(doc(db, "classes", activeClass.id), { 
                [`studentArcadeStats.${safeEmail}`]: { completed: true, stars: 3, selected: option, mistakes: mistakes } 
            });
        } else {
            playSound('error'); 
            const newMistakes = mistakes + 1;
            setMistakes(newMistakes);
            updateDoc(doc(db, "classes", activeClass.id), { 
                [`studentArcadeStats.${safeEmail}`]: { mistakes: newMistakes, completed: false, selected: option } 
            });
        }
    };

    return (
        <div style={{width: '100%', maxWidth: '600px', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '20px', alignItems: 'center' }}>
            <h1 style={{color: '#fde047', textAlign: 'center', fontSize: '32px' }}>{data.question}</h1>
            
            <div style={{background: 'var(--bg-primary)', padding: '20px', borderRadius: '16px', display: 'flex', alignItems: 'center', gap: '15px', border: '4px solid #3b82f6', boxShadow: '0 10px 20px rgba(0,0,0,0.5)', flexWrap: 'wrap', justifyContent: 'center' }}>
                {data.pieces.map((item, idx) => (
                    item === '+' || item === '-' ? 
                    <span key={idx} style={{fontSize: '40px', color: 'white', fontWeight: 'bold' }}>{item}</span> :
                    <img key={idx} src={PIECE_IMAGES[item]} alt="piece" style={{width: '80px', filter: 'drop-shadow(0 5px 10px rgba(0,0,0,0.5))' }} />
                ))}
                <span style={{fontSize: '40px', color: 'var(--text-primary)', fontWeight: 'bold' }}>= ?</span>
            </div>
            
            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '15px', width: '100%', marginTop: '20px' }}>
                {data.options.map(opt => {
                    let bg = 'var(--bg-primary)'; let border = '2px solid rgba(163, 177, 198, 0.4)';
                    if (selected === opt) {
                        if (opt === data.answer) { bg = '#10b981'; border = '2px solid #047857'; } 
                        else { bg = '#ef4444'; border = '2px solid #b91c1c'; } 
                    } else if (selected === data.answer && opt === data.answer) { bg = '#10b981'; border = '2px solid #047857'; }
                    return (
                        <button key={opt} onClick={() => handleSelect(opt)} style={{padding: '20px', fontSize: '24px', fontWeight: 'bold', color: 'white', background: bg, border: border, borderRadius: '12px', cursor: 'pointer', transition: '0.2s', boxShadow: '0 4px 6px rgba(0,0,0,0.3)' }}>
                            {opt} Points
                        </button>
                    )
                })}
            </div>
            {mistakes >= 1 && selected !== data.answer && (
                <div style={{background: 'var(--bg-primary)', color: '#fde047', padding: '15px', borderRadius: '8px', border: '1px solid #fde047', width: '100%', textAlign: 'center', fontWeight: 'bold', marginTop: '10px', fontSize: '18px', animation: 'pulse 1s infinite' }}>💡 Hint: {data.hint}</div>
            )}
        </div>
    );
};

// ==========================================
// ⚠️ DANGER ZONE
// ==========================================
const DangerZoneGame = ({ data, safeEmail, activeClass, boardWidth }) => {
    const [foundTargets, setFoundTargets] = useState([]);
    const [mistakes, setMistakes] = useState(0);

    useEffect(() => { setFoundTargets([]); setMistakes(0); }, [data.startTime]);

    const handleClick = (id) => {
        if (foundTargets.length === data.targets.length) return;
        if (data.targets.includes(id)) {
            playSound('notify');
            if (!foundTargets.includes(id)) {
                const newFound = [...foundTargets, id];
                setFoundTargets(newFound);
                const isDone = newFound.length === data.targets.length;
                if (isDone) fireConfetti();
                updateDoc(doc(db, "classes", activeClass.id), { 
                    [`studentArcadeStats.${safeEmail}`]: { 
                        foundTargets: newFound, 
                        mistakes: mistakes, 
                        completed: isDone, 
                        stars: isDone ? 3 : newFound.length 
                    } 
                });
            }
        } else {
            playSound('error'); 
            const newMistakes = mistakes + 1;
            setMistakes(newMistakes);
            updateDoc(doc(db, "classes", activeClass.id), { 
                [`studentArcadeStats.${safeEmail}`]: { 
                    foundTargets: foundTargets, 
                    mistakes: newMistakes, 
                    completed: false, 
                    stars: foundTargets.length 
                } 
            });
        }
    };

    const boardObj = {};
    if (data.fen) {
        const rowsStr = data.fen.split(' ')[0].split('/');
        const colsArr = ['a','b','c','d','e','f','g','h'];
        rowsStr.forEach((r, rIdx) => {
            let cIdx = 0;
            for(let char of r) {
                if(!isNaN(char)) cIdx += parseInt(char);
                else { boardObj[`${colsArr[cIdx]}${8-rIdx}`] = char; cIdx++; }
            }
        });
    }

    const rows = [8,7,6,5,4,3,2,1]; const cols = ['a','b','c','d','e','f','g','h'];
    return (
        <div style={{ width: `${boardWidth}px`, maxWidth: 'min(65vh, 95vw)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '15px' }}>
            <div style={{background: 'linear-gradient(90deg, #ef4444, #f97316)', padding: '15px', borderRadius: '12px', color: 'var(--text-primary)', textAlign: 'center', fontWeight: 'bold', fontSize: '20px', boxShadow: '0 4px 10px rgba(0,0,0,0.3)', border: '2px solid white' }}>
                ⚠️ {data.question} ({foundTargets.length}/{data.targets.length} found)
            </div>
            
            <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)`, border: '4px solid #ef4444', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 10px 30px rgba(239, 68, 68, 0.4)' }}>
                {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                    const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                    const isFound = foundTargets.includes(id);
                    const isPiece = boardObj[id];
                    const showHint = mistakes >= 3 && data.targets.includes(id) && !isFound;

                    return (
                        <div key={id} onClick={() => handleClick(id)} style={{position: 'relative', background: isFound ? 'rgba(239, 68, 68, 0.8)' : (showHint ? '#fde047' : (isDark ? '#769656' : '#eeeed2')), display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: '0.2s', border: isFound ? '2px solid white' : 'none' }}>
                            {isFound && <span style={{fontSize:'35px', filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.5))'}}>🔥</span>}
                            {isPiece && <img src={PIECE_IMAGES[(isPiece === isPiece.toUpperCase() ? 'w' : 'b') + isPiece.toUpperCase()]} alt="p" style={{width: '85%', zIndex: 10, pointerEvents: 'none' }} />}
                            {showHint && <span style={{fontSize:'25px', animation: 'pulse 1s infinite'}}>📍</span>}
                            <span style={{position: 'absolute', bottom: 2, right: 4, fontSize: '10px', color: isDark || isFound ? '#eeeed2' : '#769656', opacity: 0.6 }}>{id}</span>
                        </div>
                    );
                }))}
            </div>
            {mistakes >= 2 && foundTargets.length !== data.targets.length && <div style={{background: 'var(--bg-primary)', color: '#fde047', padding: '12px', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', fontSize: '16px' }}>💡 Hint: {data.hint}</div>}
        </div>
    );
};

// ==========================================
// 🍎 MINI GAME 3: APPLE MAZES
// ==========================================
const parseFenToBoard = (fen) => {
    const board = {};
    const rows = (fen || '8/8/8/8/8/8/8/8 w - - 0 1').split(' ')[0].split('/');
    const cols = ['a','b','c','d','e','f','g','h'];
    rows.forEach((row, rIdx) => {
        let cIdx = 0;
        for(let char of row) {
            if(!isNaN(char)) cIdx += parseInt(char);
            else { board[`${cols[cIdx]}${8-rIdx}`] = { type: char.toLowerCase(), color: char === char.toUpperCase() ? 'w' : 'b' }; cIdx++; }
        }
    });
    return board;
};

const isLegalMazeMove = (piece, from, to, boardObj, remainingTargets = []) => {
    const fFile = from.charCodeAt(0) - 97; const fRank = parseInt(from[1]);
    const tFile = to.charCodeAt(0) - 97; const tRank = parseInt(to[1]);
    const dx = Math.abs(tFile - fFile); const dy = Math.abs(tRank - fRank);
    const type = piece.type;
    const isPathClear = () => {
        let stepX = tFile > fFile ? 1 : (tFile < fFile ? -1 : 0); let stepY = tRank > fRank ? 1 : (tRank < fRank ? -1 : 0);
        let cx = fFile + stepX; let cy = fRank + stepY;
        while(cx !== tFile || cy !== tRank) { if (boardObj[String.fromCharCode(cx + 97) + cy]) return false; cx += stepX; cy += stepY; }
        return true;
    };
    if (type === 'r') return ((dx === 0 && dy > 0) || (dy === 0 && dx > 0)) && isPathClear();
    if (type === 'b') return (dx === dy && dx > 0) && isPathClear();
    if (type === 'q') return ((dx === dy && dx > 0) || (dx === 0 && dy > 0) || (dy === 0 && dx > 0)) && isPathClear();
    if (type === 'n') return (dx === 1 && dy === 2) || (dx === 2 && dy === 1);
    if (type === 'k') return dx <= 1 && dy <= 1 && (dx > 0 || dy > 0);
    if (type === 'p') {
        const dir = piece.color === 'w' ? 1 : -1; const startRank = piece.color === 'w' ? 2 : 7;
        if (dx === 0 && tRank - fRank === dir && !boardObj[to]) return true; 
        if (dx === 0 && fRank === startRank && tRank - fRank === 2 * dir && !boardObj[to] && !boardObj[String.fromCharCode(fFile+97)+(fRank+dir)]) return true; 
        if (dx === 1 && tRank - fRank === dir && (boardObj[to] || remainingTargets.includes(to))) return true;
        return false;
    }
    return false;
};

const generateNewFen = (boardObj) => {
    let newFen = '';
    for (let r = 8; r >= 1; r--) {
        let empty = 0;
        for (let c = 0; c < 8; c++) {
            const sq = `${['a','b','c','d','e','f','g','h'][c]}${r}`;
            const p = boardObj[sq];
            if (p) { if (empty > 0) { newFen += empty; empty = 0; } newFen += p.color === 'w' ? p.type.toUpperCase() : p.type; } else empty++;
        }
        if (empty > 0) newFen += empty; if (r > 1) newFen += '/';
    }
    return `${newFen} w - - 0 1`;
};

const MazeGame = ({ data, safeEmail, activeClass, boardWidth }) => {
    const [currentLevelIdx, setCurrentLevelIdx] = useState(0);
    const [localFen, setLocalFen] = useState('8/8/8/8/8/8/8/8 w - - 0 1');
    const [starsEarned, setStarsEarned] = useState(0);
    const [mistakesInLevel, setMistakesInLevel] = useState(0);
    const [totalMistakes, setTotalMistakes] = useState(0);
    const [isCompleted, setIsCompleted] = useState(false);
    const [remainingTargets, setRemainingTargets] = useState([]);
    const [selectedSquare, setSelectedSquare] = useState(null);
    const [showLevelUp, setShowLevelUp] = useState(false);
    const [popupText, setPopupText] = useState("YUMMY! 🍎");

    useEffect(() => {
        setIsCompleted(false); setCurrentLevelIdx(0); setStarsEarned(0); setTotalMistakes(0); setMistakesInLevel(0);
        if (data.levels?.length > 0) {
            setLocalFen(data.levels[0].fen);
            setRemainingTargets(data.levels[0].targets || []);
        }
        syncStats(0, 0, 0, false, data.levels?.[0]?.fen, []);
    }, [data.startTime]); 

    const syncStats = (level, stars, mistakes, done, fenToSync, leftTargets) => {
        if (!activeClass?.id) return;
        const updatePayload = { [`studentArcadeStats.${safeEmail}`]: { level, stars, mistakes, completed: done, remainingApples: leftTargets?.length || 0 } };
        if (fenToSync) updatePayload[`studentFens.${safeEmail}`] = fenToSync;
        updateDoc(doc(db, "classes", activeClass.id), updatePayload);
    };

    const executeMazeMove = (from, to) => {
        if (isCompleted || showLevelUp || !data?.levels) return;
        const boardObj = parseFenToBoard(localFen);
        const movingPiece = boardObj[from];

        if (movingPiece && isLegalMazeMove(movingPiece, from, to, boardObj, remainingTargets)) {
            playSound('move');
            boardObj[to] = movingPiece; delete boardObj[from];
            if (movingPiece.type === 'p' && (to[1] === '8' || to[1] === '1')) { boardObj[to].type = 'q'; toast.success("Promoted to Queen! 👑"); }
            const newFen = generateNewFen(boardObj);
            setLocalFen(newFen); setSelectedSquare(null);

            if (remainingTargets.includes(to)) {
                playSound('notify');
                const newTargets = remainingTargets.filter(t => t !== to);
                setRemainingTargets(newTargets);
                let earned = (mistakesInLevel === 0) ? 3 : (mistakesInLevel === 1 ? 2 : 1);
                const newStars = starsEarned + earned;
                setStarsEarned(newStars);

                if (newTargets.length === 0) {
                    if (currentLevelIdx + 1 < data.levels.length) {
                        setPopupText(FUN_POPUPS[Math.floor(Math.random() * FUN_POPUPS.length)]);
                        setShowLevelUp(true);
                        syncStats(currentLevelIdx + 1, newStars, totalMistakes, false, newFen, []);
                        setTimeout(() => {
                            const nextIdx = currentLevelIdx + 1; setCurrentLevelIdx(nextIdx);
                            setLocalFen(data.levels[nextIdx].fen); setRemainingTargets(data.levels[nextIdx].targets);
                            setMistakesInLevel(0); setShowLevelUp(false);
                            syncStats(nextIdx, newStars, totalMistakes, false, data.levels[nextIdx].fen, data.levels[nextIdx].targets);
                        }, 1200);
                    } else {
                        fireConfetti(); setIsCompleted(true);
                        syncStats(currentLevelIdx + 1, newStars, totalMistakes, true, newFen, []);
                    }
                } else { syncStats(currentLevelIdx, newStars, totalMistakes, false, newFen, newTargets); }
            } else { syncStats(currentLevelIdx, starsEarned, totalMistakes, false, newFen, remainingTargets); }
        } else {
            if (to !== from) {
                playSound('error'); setMistakesInLevel(m => m + 1); setTotalMistakes(prev => prev + 1);
                syncStats(currentLevelIdx, starsEarned, totalMistakes + 1, false, localFen, remainingTargets);
                setSelectedSquare(null);
            }
        }
    };

    const handleSquareClick = (id) => {
        const boardObj = parseFenToBoard(localFen);
        const pieceOnSquare = boardObj[id];

        if (selectedSquare) {
            executeMazeMove(selectedSquare, id);
        } else { 
            if (pieceOnSquare) setSelectedSquare(id); 
        }
    };

    const rows = [8,7,6,5,4,3,2,1]; const cols = ['a','b','c','d','e','f','g','h'];
    const displayBoard = parseFenToBoard(localFen);

    return (
        <div style={{ width: `${boardWidth}px`, maxWidth: 'min(65vh, 95vw)', margin: '0 auto', display: 'flex', flexDirection: 'column', gap: '15px', position: 'relative' }}>
            <style>{` @keyframes floatApple { 0%, 100% { transform: translateY(0) scale(1); filter: drop-shadow(0 0 5px rgba(239, 68, 68, 0.5)); } 50% { transform: translateY(-5px) scale(1.1); filter: drop-shadow(0 10px 10px rgba(239, 68, 68, 0.8)); } } @keyframes popIn { 0% { transform: scale(0.5); opacity: 0; } 100% { transform: scale(1); opacity: 1; } } `}</style>

            <div style={{background: 'linear-gradient(90deg, #10b981, #3b82f6)', padding: '12px 20px', borderRadius: '12px', color: 'var(--text-primary)', textAlign: 'center', boxShadow: 'var(--panel-shadow)', border: '2px solid white' }}>
                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{fontWeight: 'bold', fontSize: '18px' }}>Level {currentLevelIdx + 1} / {data.levels?.length || 0}</div>
                    <div style={{fontSize: '20px', fontWeight: 'bold', color: '#fde047' }}>🍎 {starsEarned}</div>
                </div>
            </div>

            {showLevelUp && <div style={{position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', background: '#ec4899', padding: '15px 30px', borderRadius: '50px', color: 'var(--text-primary)', fontSize: '28px', fontWeight: 'bold', zIndex: 100, border: '4px solid white', whiteSpace: 'nowrap', boxShadow: 'var(--panel-shadow)', animation: 'popIn 0.3s ease-out' }}>{popupText}</div>}
            {isCompleted && <div style={{position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', background: 'rgba(0,0,0,0.85)', zIndex: 110, display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', borderRadius: '12px', animation: 'popIn 0.5s ease-out' }}><div style={{fontSize: '80px', animation: 'bounce 2s infinite' }}>🏆</div><h1 style={{color: '#10b981', margin: '10px 0', textShadow: '0 0 10px #10b981', fontSize: '36px' }}>YOU WIN!</h1><div style={{color: '#fde047', fontSize: '24px', fontWeight: 'bold' }}>Total Apples: {starsEarned} 🍎</div></div>}

            <div style={{width: '100%', aspectRatio: '1/1', display: 'grid', gridTemplateColumns: `repeat(8, 1fr)`, gridTemplateRows: `repeat(8, 1fr)`, background: 'rgba(163, 177, 198, 0.4)', borderRadius: '8px', overflow: 'hidden', border: '4px solid var(--bg-primary)', boxShadow: '0 10px 20px rgba(0,0,0,0.5)' }}>
                {rows.map((row, rIdx) => cols.map((col, cIdx) => {
                    const id = `${col}${row}`; const isDark = (rIdx + cIdx) % 2 === 1;
                    let isTarget = remainingTargets.includes(id); let pieceObj = displayBoard[id];
                    return (
                        <div key={id} 
                            onClick={() => handleSquareClick(id)} 
                            onDragOver={e => e.preventDefault()}
                            onDrop={e => {
                                e.preventDefault();
                                const fromSq = e.dataTransfer.getData('fromSq');
                                if (fromSq && fromSq !== id) executeMazeMove(fromSq, id);
                            }}
                            style={{position: 'relative', background: selectedSquare === id ? '#2563eb' : (isDark ? '#769656' : '#eeeed2'), display: 'flex', justifyContent: 'center', alignItems: 'center', cursor: 'pointer', transition: 'background 0.2s' }}>
                            
                            {isTarget && !pieceObj && <span style={{fontSize: '35px', animation: 'floatApple 2s infinite', userSelect: 'none', filter: 'drop-shadow(0 4px 5px rgba(0,0,0,0.5))', pointerEvents: 'none'}}>🍎</span>}
                            
                            {pieceObj && <img 
                                src={PIECE_IMAGES[pieceObj.color + pieceObj.type.toUpperCase()]} 
                                alt="p" 
                                draggable={true}
                                onDragStart={e => {
                                    e.dataTransfer.setData('fromSq', id);
                                    setSelectedSquare(id);
                                }}
                                onClick={(e) => { e.stopPropagation(); handleSquareClick(id); }}
                                style={{width: '90%', zIndex: 10, filter: 'drop-shadow(0 4px 6px rgba(0,0,0,0.4))', animation: 'popIn 0.3s ease-out' }} 
                            />}
                            <span style={{position: 'absolute', bottom: 2, right: 4, fontSize: '10px', color: isDark ? '#eeeed2' : '#769656', opacity: 0.5 }}>{id}</span>
                        </div>
                    );
                }))}
            </div>

            {data.levels?.[currentLevelIdx]?.hint && <div style={{background: 'var(--bg-primary)', color: '#2563eb', padding: '12px', borderRadius: '8px', textAlign: 'center', fontWeight: 'bold', border: '1px solid rgba(163, 177, 198, 0.4)', fontSize: '16px' }}>💡 Hint: {data.levels[currentLevelIdx].hint}</div>}
        </div>
    );
};

// ==========================================
// 🚀 MASTER ENGINE: ROUTER
// ==========================================
export default function ArcadeBoard({ activeClass, userEmail }) {
    const [boardWidth, setBoardWidth] = useState(400);
    const arcadeData = activeClass?.arcadeData;
    const safeEmail = userEmail?.replace(/\./g, '_');

    useEffect(() => { setBoardWidth(Math.min(window.innerHeight - 200, window.innerWidth - 40, 500)); }, []);

    if (!arcadeData) return <div style={{color:'var(--text-primary)', textAlign:'center', padding:'50px'}}>🎲 Waiting for Coach...</div>;

    if (arcadeData.mode === 'MCQ') return <MCQGame data={arcadeData} safeEmail={safeEmail} activeClass={activeClass} />;
    if (arcadeData.mode === 'MATH') return <MathGame data={arcadeData} safeEmail={safeEmail} activeClass={activeClass} />;
    if (arcadeData.mode === 'AWARENESS') return <AwarenessGame data={arcadeData} safeEmail={safeEmail} activeClass={activeClass} boardWidth={boardWidth} />;
    if (arcadeData.mode === 'DANGER') return <DangerZoneGame data={arcadeData} safeEmail={safeEmail} activeClass={activeClass} boardWidth={boardWidth} />;
    if (arcadeData.mode === 'MAZE') return <MazeGame data={arcadeData} safeEmail={safeEmail} activeClass={activeClass} boardWidth={boardWidth} />;

    return null;
}
